// Mobile-tech payment collection service — thin wrappers over the existing web
// invoice/payment endpoints. The web client invokes these endpoints inline from
// requestimates.jsx (createInvoiceExplicitly, materializeInvoice, handleSettle,
// settleOffline, etc.); this service hoists the same primitives so the mobile
// PaymentCollectionScreen can reuse them without dragging in the RequestImate
// class component.
//
// Endpoints below are verified against requestimates.jsx as of 2026-05-15.
// No new server work for v1 — every call here exists today.

import { HTTPService } from './httpService';

const API = new HTTPService();

/**
 * POST /invoices/ — create the invoice for a request.
 *
 * Mirrors createInvoiceExplicitly in requestimates.jsx:1859. Returns either a
 * draft envelope `{ draft: true, paymentId, amount }` (when the request is new
 * and the installer chooses an offline-friendly method) or a real Stripe
 * invoice envelope `{ invoiceId }` for retail-card / dealer flows.
 *
 * The mobile state machine fires this immediately after the last service of a
 * stop finishes — same trigger semantics as handleCompleteEstimate on the web.
 */
export const createInvoice = async (requestId, requestType) => {
  const body = { invoiceId: requestId, invoiceType: requestType };
  const { data } = await API.post('/invoices/', body);
  if (data && data.error) {
    const err = new Error(data.message || 'Unable to create invoice for this request.');
    err.stripeNotReady = !!data.stripeNotReady;
    err.reason = data.reason;
    throw err;
  }
  return data;
};

/**
 * POST /invoices/:paymentId/materialize — convert a draft into a real Stripe
 * invoice with the chosen payment method. Mirrors materializeInvoice in
 * requestimates.jsx:1894.
 *
 * paymentMethod values match the web mapping: 'card' | 'cash' | 'check' |
 * 'terminal' | 'split'. paymentToken is required for split-payment flows;
 * irrelevant for the four mobile chips.
 */
export const materializeInvoice = async ({ paymentId, paymentMethod, paymentToken, checkNumber }) => {
  const body = { paymentMethod };
  if (paymentToken) body.paymentToken = paymentToken;
  if (paymentMethod === 'check' && checkNumber) body.checkNumber = checkNumber;
  const { data } = await API.post(`/invoices/${paymentId}/materialize`, body);
  if (data && data.success === false) {
    const err = new Error(data.message || data.reason || 'Materialization failed');
    err.reason = data.reason;
    err.locationName = data.locationName;
    throw err;
  }
  return data;
};

/**
 * PUT /invoices/settle/:invoiceId — marks the Stripe invoice as paid out-of-band.
 * Mirrors settleInvoice in requestimates.jsx:1347. Used for cash/check after
 * materialize succeeds.
 */
export const settleInvoice = async (invoiceId) => {
  await API.put(`/invoices/settle/${invoiceId}`);
};

/**
 * PUT /installerPaymentSettle/:requestId — records the payment row on the
 * installer-payments table. Mirrors updatePayment in requestimates.jsx:1237.
 */
export const recordInstallerPayment = async ({ requestId, installerId, paymentMethod, paymentDate }) => {
  const body = {
    requestId,
    paymentDate: paymentDate || new Date().toISOString().slice(0, 19).replace('T', ' '),
    installerId,
    paymentState: 'settled',
    paymentMethod: paymentMethod || null,
  };
  await API.put(`/installerPaymentSettle/${requestId}`, body);
};

/**
 * PUT /installerPaymentCheck/:requestId — stores the check number on the
 * installer-payments table. Mirrors updateInstallerPayment in
 * requestimates.jsx:1422. The check number rides in the `payment_id` field
 * (legacy naming — do not confuse with the Stripe paymentId).
 */
export const recordCheckNumber = async (requestId, checkNumber) => {
  await API.put(`/installerPaymentCheck/${requestId}`, { payment_id: checkNumber });
};

/**
 * POST /invoices/:paymentId/settle-offline — direct DB settle for cash/check
 * when Stripe materialization is unavailable. Mirrors settleOffline in
 * requestimates.jsx:3981.
 */
export const settleOffline = async ({ paymentId, paymentMethod, checkNumber }) => {
  const body = { paymentMethod };
  if (paymentMethod === 'check' && checkNumber) body.checkNumber = checkNumber;
  const { data } = await API.post(`/invoices/${paymentId}/settle-offline`, body);
  return data;
};

/**
 * GET /invoices/:invoiceId — fetch a Stripe Invoice's full state, including
 * `.paid` (boolean) and `.status` ('draft' | 'open' | 'paid' | 'void' | ...).
 * Mirrors readInvoice in requestimates.jsx:1001. Used by PaymentCollectionScreen
 * to detect pre-paid invoices: the atomic complete endpoint can send the
 * customer a hosted pay link automatically, so by the time the tech reaches
 * Collect Payment the customer may have already paid on their own device.
 */
export const fetchInvoice = async (invoiceId) => {
  const { data } = await API.get(`/invoices/${invoiceId}`);
  return data;
};

/**
 * GET /invoices/:invoiceId/fresh-url — Stripe hosted pages expire after
 * 30-120 days, so always refresh before opening. Mirrors the fresh-url branch
 * of handlePayment in requestimates.jsx:4272.
 */
export const getFreshUrl = async (invoiceId) => {
  const { data } = await API.get(`/invoices/${invoiceId}/fresh-url`);
  if (!data || !data.success || !data.hosted_invoice_url) {
    throw new Error('Unable to refresh the invoice link. Please try again.');
  }
  return data.hosted_invoice_url;
};

/**
 * PUT /invoices/resend/:id — re-sends the customer the work-complete email
 * containing the hosted pay link. Mirrors handleResendDraftEmail /
 * resendInvoice in requestimates.jsx:1373 / 4013. The id can be the request
 * id (numeric, for draft resend) or the Stripe invoice id (string).
 */
export const sendPayLink = async (idOrInvoiceId) => {
  await API.put(`/invoices/resend/${idOrInvoiceId}`);
};

/**
 * POST /requestState — appends a state row (e.g. 'paid' / 'finished') to the
 * request's state history. Mirrors addRequestState in requestimates.jsx:1111.
 * Accepts optional user + location context — server stores it on the row for
 * audit trail. Older calls (no context) keep working — server defaults the
 * missing fields.
 */
export const addRequestState = async (requestId, status, note, ctx = {}) => {
  const body = {
    requestId,
    requestStatus: status,
    requestNote: note || null,
  };
  if (ctx.userId != null) body.changedByUserId = ctx.userId;
  if (ctx.userType) body.changedByUserType = ctx.userType;
  if (ctx.locationId != null) body.location_id = ctx.locationId;
  if (ctx.serviceId != null) body.serviceId = ctx.serviceId;
  await API.post('/requestState', body);
};

/**
 * Mark a request as fully completed via the atomic server endpoint.
 *
 * POST /requests/:requestId/complete — wraps in a single MySQL transaction:
 *   - UPDATE requests SET current_state='finished', available_date=NOW()
 *   - UPDATE request_offered SET current_status='finished', date_updated=NOW()
 *   - UPDATE installer_calendar SET work_status='finished'
 *   - INSERT INTO request_state (audit row)
 *
 * If createInvoice !== false (default true), the server then attempts invoice
 * creation as a follow-on AFTER the state transaction commits. Stripe-side
 * state can't be rolled back, so invoice failure does NOT undo the state
 * changes — the response still succeeds with `invoiceId: null` and
 * `invoiceError: "..."`. Client can retry the invoice via POST /invoices/
 * without re-running the state transition.
 *
 * Returns `{ request, invoice, invoiceError }` — `invoice` is the createInvoice
 * envelope shape (`{invoiceId}` for real Stripe invoices OR
 * `{draft, paymentId, amount}` for drafts), or `null` if creation failed.
 *
 * On state-transaction failure throws an Error with `.detail` populated by the
 * server's `{ error, detail }` body — caller should bail and surface the error.
 */
export const completeRequest = async ({ requestId, installerId, locationId, userId, userType, completedAt, createInvoice = true }) => {
  const body = {
    installerId,
    locationId: locationId != null ? locationId : null,
    changedByUserId: userId,
    changedByUserType: userType,
    createInvoice,
  };
  if (completedAt) body.completedAt = completedAt;

  const { data } = await API.post(`/requests/${requestId}/complete`, body);

  if (!data || data.success === false) {
    const err = new Error(data?.error || 'Failed to complete request');
    err.detail = data?.detail;
    throw err;
  }

  // Server returns invoice fields at the top level (matches the envelope
  // createInvoiceExplicitly returns: `{invoiceId, paymentId, draft, amount?}`).
  // Normalize into a single `invoice` object for PaymentCollectionScreen and
  // the dealer banner — they both expect that shape. Null when neither
  // invoiceId nor paymentId came back (server skipped invoice creation, or
  // it failed and invoiceError is set instead).
  let invoice = null;
  if (data.invoiceId || data.paymentId) {
    invoice = {
      invoiceId: data.invoiceId || null,
      paymentId: data.paymentId || null,
      draft: !!data.draft,
      amount: data.amount != null ? data.amount : null,
    };
  }

  return {
    request: data.request || null,
    invoice,
    invoiceError: data.invoiceError || null,
  };
};

// ─── Higher-level orchestrators used by PaymentCollectionScreen ────────────
//
// These mirror the cash/check/online branches of handlePayment in
// requestimates.jsx:4197 — same draft-vs-materialized branching, same
// offline-fallback when materialize fails on cash/check.

/**
 * Mobile "Pay now" — open the Stripe hosted page on the tech's device. Customer
 * (or the tech, on the customer's behalf) enters card on the hosted page.
 *
 * If invoice is still a draft, materialize first as a card invoice so the
 * hosted URL is available. No state change to the request — Stripe webhook
 * handles settlement async, just like the web's "Pay Online" branch.
 *
 * Returns { hostedUrl } — caller decides whether to window.open or surface to UI.
 */
export const collectViaCardNow = async (invoice) => {
  let invoiceId = invoice.invoiceId;
  if (invoice.draft || !invoiceId) {
    const result = await materializeInvoice({
      paymentId: invoice.paymentId,
      paymentMethod: 'card',
    });
    invoiceId = result.invoiceId || invoiceId;
  }
  if (!invoiceId) throw new Error('Invoice could not be materialized.');
  const hostedUrl = await getFreshUrl(invoiceId);
  return { hostedUrl, invoiceId };
};

/**
 * Mobile "Send pay link" — same materialize-if-draft path as Pay now, then
 * trigger resendInvoice to email/SMS the customer the hosted URL.
 */
export const collectViaCardLink = async (invoice) => {
  let invoiceId = invoice.invoiceId;
  if (invoice.draft || !invoiceId) {
    const result = await materializeInvoice({
      paymentId: invoice.paymentId,
      paymentMethod: 'card',
    });
    invoiceId = result.invoiceId || invoiceId;
  }
  if (!invoiceId) throw new Error('Invoice could not be materialized.');
  await sendPayLink(invoiceId);
  return { invoiceId };
};

/**
 * Mobile "Cash collected" — try materialize-as-cash, fall back to settle-offline
 * if Stripe is unavailable, then settle + record installer payment + flip
 * request state to 'paid'. Mirrors the cash branch of handlePayment.
 */
export const collectViaCash = async (invoice, { requestId, installerId }) => {
  let invoiceId = invoice.invoiceId;

  if (invoice.draft || !invoiceId) {
    try {
      const result = await materializeInvoice({
        paymentId: invoice.paymentId,
        paymentMethod: 'cash',
      });
      invoiceId = result.invoiceId || invoiceId;
    } catch (err) {
      // Stripe-side failure for cash is recoverable — fall back to offline.
      console.warn('paymentService: materialize-cash failed, falling back to settle-offline', err);
      await settleOffline({ paymentId: invoice.paymentId, paymentMethod: 'cash' });
      await addRequestState(requestId, 'paid', null);
      return { method: 'cash', offline: true };
    }
  }

  if (invoiceId) await settleInvoice(invoiceId);
  await recordInstallerPayment({ requestId, installerId, paymentMethod: 'Cash' });
  await addRequestState(requestId, 'paid', null);
  return { method: 'cash', invoiceId };
};

/**
 * Mobile "Check collected" — same shape as cash plus the check-number capture
 * step. checkNumber is optional in v1 (tech may not have it on hand).
 */
export const collectViaCheck = async (invoice, { requestId, installerId, checkNumber }) => {
  let invoiceId = invoice.invoiceId;

  if (invoice.draft || !invoiceId) {
    try {
      const result = await materializeInvoice({
        paymentId: invoice.paymentId,
        paymentMethod: 'check',
        checkNumber,
      });
      invoiceId = result.invoiceId || invoiceId;
    } catch (err) {
      console.warn('paymentService: materialize-check failed, falling back to settle-offline', err);
      await settleOffline({
        paymentId: invoice.paymentId,
        paymentMethod: 'check',
        checkNumber,
      });
      if (checkNumber) await recordCheckNumber(requestId, checkNumber);
      await addRequestState(requestId, 'paid', null);
      return { method: 'check', offline: true, checkNumber };
    }
  }

  if (invoiceId) await settleInvoice(invoiceId);
  await recordInstallerPayment({ requestId, installerId, paymentMethod: 'Check' });
  if (checkNumber) await recordCheckNumber(requestId, checkNumber);
  await addRequestState(requestId, 'paid', null);
  return { method: 'check', invoiceId, checkNumber };
};

const PaymentService = {
  createInvoice,
  materializeInvoice,
  settleInvoice,
  recordInstallerPayment,
  recordCheckNumber,
  settleOffline,
  fetchInvoice,
  getFreshUrl,
  sendPayLink,
  addRequestState,
  completeRequest,
  collectViaCardNow,
  collectViaCardLink,
  collectViaCash,
  collectViaCheck,
};

export default PaymentService;
